import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  getInvitations,
  acceptInvitation,
  declineInvitation,
} from '../services/membershipService.js'
import { useToastStore } from '../stores/toastStore.js'

/**
 * Pending invitations for the current user. Refetches on window focus so the
 * TopBar bell badge stays current when the user returns to the tab.
 */
export function useInvitations() {
  return useQuery({
    queryKey: ['invitations'],
    queryFn: getInvitations,
    refetchOnWindowFocus: true,
  })
}

/**
 * Accept / decline mutations for an invitation.
 * - accept  → toast + redirect to the dashboard; invalidates invitations + memberships.
 * - decline → durable server-side delete; invalidates invitations (card disappears).
 */
export function useInvitationActions() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const addToast = useToastStore((s) => s.addToast)

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['invitations'] })
    queryClient.invalidateQueries({ queryKey: ['memberships'] })
  }

  const accept = useMutation({
    mutationFn: (orgId) => acceptInvitation(orgId),
    onSuccess: (membership) => {
      invalidate()
      addToast('success', `You've joined ${membership?.organisationName ?? 'the organisation'}`)
      navigate('/')
    },
    onError: () => addToast('error', 'Could not accept the invitation. Please try again.'),
  })

  const decline = useMutation({
    mutationFn: (orgId) => declineInvitation(orgId),
    onSuccess: () => invalidate(),
    onError: () => addToast('error', 'Could not decline the invitation. Please try again.'),
  })

  return { accept, decline }
}
